import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { BrandInfo } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { BrandManualDocument } from '../components/brand/BrandManualDocument';

// Vista standalone del Manual de marca (ruta /workspace/:id/brand/manual, abierta
// en pestaña nueva sin chrome de la app). Toolbar para imprimir/guardar PDF que
// se oculta al imprimir. El documento usa lo ÚLTIMO guardado en brand_info.
//
// PÚBLICA: lee del endpoint /api/public/brand-manual/:id (sin auth ni paywall) para
// que el link sea compartible. Funciona igual para el dueño logueado y para
// cualquiera con el link.

interface PublicBrandManual {
  workspaceName: string;
  logoUrl: string | null;
  brand: BrandInfo | null;
}

// Fondo gris claro alrededor de la hoja; blanco puro al imprimir.
const ManualGlobal = createGlobalStyle`
  body { background: #F2F2F4; }
  @media print {
    body { background: #FFFFFF; }
    @page { margin: 14mm; }
  }
`;

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #18181B;
  color: #fff;

  @media print { display: none; }
`;

const Spacer = styled.span`
  margin-right: auto;
  font-size: 13px;
  color: #A1A1AA;
`;

const TBtn = styled.button<{ $primary?: boolean }>`
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : '#3F3F46')};
  background: ${({ $primary }) => ($primary ? '#4F46E5' : 'transparent')};
  color: #fff;
  &:hover { opacity: 0.9; }
`;

const Center = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #52525B;
  font-family: system-ui, sans-serif;
`;

export function BrandManualPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [brand, setBrand] = useState<BrandInfo | null>(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');

  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const data = await apiClient.get<PublicBrandManual>(`/api/public/brand-manual/${workspaceId}`);
        setWorkspaceName(data.workspaceName || '');
        setLogoUrl(data.logoUrl || null);
        if (data.brand) {
          setBrand(data.brand);
          setState('ready');
        } else {
          setState('empty');
        }
      } catch {
        setState('error');
      }
    })();
  }, [workspaceId]);

  const generatedAt = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <ManualGlobal />
      <Toolbar>
        <Spacer>Brand manual · {workspaceName || '…'}</Spacer>
        <TBtn onClick={() => window.close()}>Close</TBtn>
        <TBtn $primary onClick={() => window.print()} disabled={state !== 'ready'}>
          Print / Save PDF
        </TBtn>
      </Toolbar>

      {state === 'loading' && <Center>Loading manual…</Center>}
      {state === 'error' && <Center>Couldn't load the manual. Close this tab and try again.</Center>}
      {state === 'empty' && (
        <Center>
          No brand data yet.<br />
          Fill in the profile (and use Sources to enrich it), then open the manual again.
        </Center>
      )}
      {state === 'ready' && brand && (
        <BrandManualDocument brand={brand} workspaceName={workspaceName} logoUrl={logoUrl} generatedAt={generatedAt} />
      )}
    </>
  );
}
